import { Droplet, WifiOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { SensorDevice } from '../../services/sensorDevices';

interface TankLevelCardProps {
  device: SensorDevice;
}

/**
 * Type-specific card for tank_level sensors.
 * `device.latest_value` is percent full (0–100).
 * Optional config:
 *   - tank_capacity_liters: total tank capacity for computing litres from %
 *   - alarm_low_pct: below this, tint red
 */
export default function TankLevelCard({ device }: TankLevelCardProps) {
  const percent = typeof device.latest_value === 'number'
    ? Math.max(0, Math.min(100, device.latest_value))
    : null;

  const config = (device.config || {}) as {
    tank_capacity_liters?: number;
    alarm_low_pct?: number;
  };
  const alarmLow = typeof config.alarm_low_pct === 'number' ? config.alarm_low_pct : 15;
  const capacityL = typeof config.tank_capacity_liters === 'number' ? config.tank_capacity_liters : null;
  const litres = percent !== null && capacityL ? Math.round((percent / 100) * capacityL) : null;

  const isLow = percent !== null && percent < alarmLow;
  const fillColor = isLow ? 'bg-red-500' : percent !== null && percent < 40 ? 'bg-amber-500' : 'bg-blue-500';
  const borderColor = isLow ? 'border-red-300' : 'border-gray-200';

  return (
    <Link
      to={`/irrigation/sensors/${encodeURIComponent(device.device_id)}`}
      className={`block rounded-xl border ${borderColor} bg-white p-4 shadow-sm hover:shadow-md transition-shadow`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Droplet className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800 truncate">
              {device.name || device.device_id}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {device.type_display_name || 'Tank Level'}
            </div>
          </div>
        </div>
        {!device.online && <WifiOff className="w-4 h-4 text-gray-400" />}
      </div>

      <div className="mt-3 flex items-end gap-3">
        {/* Fill indicator */}
        <div className="relative w-10 h-20 rounded-md border border-gray-300 bg-gray-50 overflow-hidden flex-shrink-0">
          {percent !== null && (
            <div
              className={`absolute bottom-0 left-0 right-0 ${fillColor} transition-[height] duration-500`}
              style={{ height: `${percent}%` }}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className={`text-2xl font-bold ${isLow ? 'text-red-600' : 'text-gray-800'}`}>
            {percent !== null ? `${percent.toFixed(1)}%` : '—'}
          </div>
          {litres !== null && (
            <div className="text-xs text-gray-500">{litres.toLocaleString()} L</div>
          )}
          {isLow && (
            <div className="text-xs text-red-600 mt-0.5">Below {alarmLow}%</div>
          )}
        </div>
      </div>

      {device.latest_reading_time && (
        <div className="mt-2 text-[11px] text-gray-400">
          Last: {new Date(device.latest_reading_time).toLocaleString()}
        </div>
      )}
    </Link>
  );
}
